import React, { useEffect, useMemo, useState } from "react";
import Footer from "./Footer";
import Header from "./Header";
import axios from "axios";
import Swal from "sweetalert2";
import config from "../../config";

const MODULES = [
  { key: "order", label: "Orders", icon: "bi bi-bag-check" },
  { key: "shipment", label: "Shipments", icon: "bi bi-truck" },
  { key: "pickup", label: "Pickup Requests", icon: "bi bi-arrow-up-square" },
  { key: "ndr", label: "NDR", icon: "bi bi-exclamation-octagon" },
  {
    key: "weightdisc",
    label: "Weight Discrepancy",
    icon: "bi bi-box2",
  },
  { key: "ratecard", label: "Rate Card", icon: "bi bi-receipt" },
  { key: "warehouse", label: "Warehouse", icon: "bi bi-box-seam" },
  {
    key: "ratecalc",
    label: "Rate Calculator",
    icon: "bi bi-calculator",
  },
  {
    key: "allocation",
    label: "Order Allocation",
    icon: "bi bi-diagram-3",
  },
  {
    key: "shippingrules",
    label: "Shipping Rules",
    icon: "bi bi-journal-bookmark",
  },
  {
    key: "notifsettings",
    label: "Notification Settings",
    icon: "bi bi-bell",
  },
  {
    key: "aftership",
    label: "AfterShip",
    icon: "bi bi-truck-front",
  },
  { key: "boxes", label: "Boxes", icon: "bi bi-box" },
  {
    key: "shippingcharges",
    label: "Shipping Charges",
    icon: "bi bi-truck",
  },
  {
    key: "codremittance",
    label: "COD Remittance",
    icon: "bi bi-cash-coin",
  },
  {
    key: "passbook",
    label: "Passbook",
    icon: "bi bi-journal-text",
  },
  {
    key: "deduction",
    label: "Deduction",
    icon: "bi bi-dash-circle",
  },
  {
    key: "recharge",
    label: "Recharge",
    icon: "bi bi-plus-circle",
  },
  {
    key: "refund",
    label: "Refund",
    icon: "bi bi-arrow-counterclockwise",
  },
  {
    key: "weightrecon",
    label: "Weight Reconciliation",
    icon: "bi bi-arrow-left-right",
  },
  {
    key: "shpreports",
    label: "Shipment Reports",
    icon: "bi bi-bar-chart-line",
  },
  {
    key: "invoices",
    label: "Invoices",
    icon: "bi bi-file-earmark-text",
  },
  {
    key: "team",
    label: "Team",
    icon: "bi bi-people",
  },
  {
    key: "tickets",
    label: "Support Tickets",
    icon: "bi bi-headset",
  },
  {
    key: "faq",
    label: "Support FAQs",
    icon: "bi bi-question-circle",
  },
  {
    key: "channels",
    label: "Channel Integration",
    icon: "bi bi-broadcast",
  },
  {
    key: "apis",
    label: "Developer APIs",
    icon: "bi bi-code-slash",
  },
  {
    key: "labelsettings",
    label: "Label Settings",
    icon: "bi bi-tag",
  },
];

const getUserData = () => {
  try {
    const data = localStorage.getItem("ship_rocket_user");

    if (!data) {
      return {};
    }

    return JSON.parse(data);
  } catch (error) {
    console.log("Invalid ship_rocket_user", error);
    return {};
  }
};

const normalizePrivileges = (privileges = {}) => {
  const result = {};

  MODULES.forEach((module) => {
    /*
     * New format:
     *
     * order: true
     * shipment: false
     *
     * Also temporarily support the old format:
     *
     * order: {
     *   view: true
     * }
     *
     * This makes frontend migration safer.
     */
    if (typeof privileges[module.key] === "boolean") {
      result[module.key] = privileges[module.key];
    } else if (
      privileges[module.key] &&
      typeof privileges[module.key] === "object"
    ) {
      result[module.key] = privileges[module.key].view === true;
    } else {
      result[module.key] = false;
    }
  });

  return result;
};

const initials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);

  if (!parts.length) {
    return "?";
  }

  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }

  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const Team = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [activeTab, setActiveTab] = useState("all");

  const [search, setSearch] = useState("");

  const userData = getUserData();

  const isTeamMember = Number(userData?.user_type) === 2;

  const canViewTeam =
    !isTeamMember ||
    userData?.previlage?.team === true ||
    userData?.previlage?.team?.view === true;

  const canAddTeam =
    !isTeamMember ||
    userData?.previlage?.team === true ||
    userData?.previlage?.team?.add === true;

  const canDeleteTeam =
    !isTeamMember ||
    userData?.previlage?.team === true ||
    userData?.previlage?.team?.delete === true;

  /*
   * DO NOT load elite.js here.
   *
   * Footer.jsx already loads it.
   *
   * Loading elite.js here again causes:
   *
   * Failed to execute 'removeChild' on 'Node'
   */

  const getTeamMembers = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        `${config.apiUrl}/user/get_team_members`,
        {},
        {
          headers: config.headers,
        }
      );

      console.log("get_team_members response =>", response.data);

      if (response.data?.status === true) {
        setMembers(
          Array.isArray(response.data.output) ? response.data.output : []
        );
      } else {
        setMembers([]);

        Swal.fire({
          icon: "error",
          text: response.data?.message || "Unable to fetch team members.",
        });
      }
    } catch (error) {
      console.log("get_team_members error =>", error);

      setMembers([]);

      Swal.fire({
        icon: "error",
        text: error?.response?.data?.message || "Unable to fetch team members.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canViewTeam) {
      getTeamMembers();
    }
  }, [canViewTeam]);

  const formattedMembers = useMemo(() => {
    return members.map((member) => {
      const privileges = normalizePrivileges(
        member?.previlage || member?.perms || {}
      );

      const selectedModules = MODULES.filter(
        (module) => privileges[module.key] === true
      ).length;

      return {
        ...member,

        id: member?.staff_id || member?.id || "",

        name: member?.full_name || member?.name || "",

        email: member?.email || "",

        mobile: member?.mobile || "",

        role: member?.role || "Member",

        warehouse: member?.warehouse || "All India",

        status: Number(member?.dstatus) === 1 ? "active" : "disabled",

        privileges,

        selectedModules,
      };
    });
  }, [members]);

  const counts = useMemo(() => {
    return {
      all: formattedMembers.length,

      active: formattedMembers.filter((item) => item.status === "active")
        .length,

      disabled: formattedMembers.filter((item) => item.status === "disabled")
        .length,
    };
  }, [formattedMembers]);

  const filteredMembers = useMemo(() => {
    const searchValue = search.trim().toLowerCase();

    return formattedMembers.filter((member) => {
      if (activeTab === "active" && member.status !== "active") {
        return false;
      }

      if (activeTab === "disabled" && member.status !== "disabled") {
        return false;
      }

      if (!searchValue) {
        return true;
      }

      const value = [
        member.name,
        member.email,
        member.mobile,
        member.role,
        member.warehouse,
      ]
        .join(" ")
        .toLowerCase();

      return value.includes(searchValue);
    });
  }, [formattedMembers, activeTab, search]);

  const handleStatusChange = async (member, checked) => {
    if (!member.id) {
      return;
    }

    if (!canAddTeam) {
      Swal.fire({
        icon: "warning",
        text: "You do not have permission to change team members.",
      });

      return;
    }

    try {
      const response = await axios.post(
        `${config.apiUrl}/user/change_team_member_status`,
        {
          staff_id: member.id,
          dstatus: checked ? 1 : 0,
        },
        {
          headers: config.headers,
        }
      );

      if (response.data?.status === true) {
        setMembers((previous) =>
          previous.map((item) =>
            (item.staff_id || item.id) === member.id
              ? {
                  ...item,
                  dstatus: checked ? 1 : 0,
                }
              : item
          )
        );

        Swal.fire({
          toast: true,
          position: "bottom-end",
          icon: "success",
          title: checked ? "Member enabled." : "Member disabled.",
          showConfirmButton: false,
          timer: 1500,
        });
      } else {
        Swal.fire({
          icon: "error",
          text: response.data?.message || "Unable to update member status.",
        });
      }
    } catch (error) {
      console.log("change status error =>", error);

      Swal.fire({
        icon: "error",
        text:
          error?.response?.data?.message || "Unable to update member status.",
      });
    }
  };

  const handleDelete = async (member) => {
    if (!canDeleteTeam) {
      Swal.fire({
        icon: "warning",
        text: "You do not have permission to delete team members.",
      });

      return;
    }

    const confirm = await Swal.fire({
      icon: "warning",
      title: `Delete "${member.name}"?`,
      text: "This cannot be undone.",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
    });

    if (!confirm.isConfirmed) {
      return;
    }

    try {
      const response = await axios.post(
        `${config.apiUrl}/user/delete_team_member`,
        {
          staff_id: member.id,
        },
        {
          headers: config.headers,
        }
      );

      if (response.data?.status === true) {
        setMembers((previous) =>
          previous.filter((item) => (item.staff_id || item.id) !== member.id)
        );

        Swal.fire({
          toast: true,
          position: "bottom-end",
          icon: "success",
          title: "Member deleted.",
          showConfirmButton: false,
          timer: 1500,
        });
      } else {
        Swal.fire({
          icon: "error",
          text: response.data?.message || "Unable to delete member.",
        });
      }
    } catch (error) {
      console.log("delete member error =>", error);

      Swal.fire({
        icon: "error",
        text: error?.response?.data?.message || "Unable to delete member.",
      });
    }
  };

  const handleEdit = (member) => {
    if (!canAddTeam) {
      Swal.fire({
        icon: "warning",
        text: "You do not have permission to edit team members.",
      });

      return;
    }

    window.location.href = `/create_team_user?id=${encodeURIComponent(
      member.id
    )}`;
  };

  const handleDuplicate = (member) => {
    if (!canAddTeam) {
      Swal.fire({
        icon: "warning",
        text: "You do not have permission to add team members.",
      });

      return;
    }

    window.location.href = `/create_team_user?id=new&duplicate=${encodeURIComponent(
      member.id
    )}`;
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();

        const input = document.querySelector("#tmSearch input");

        input?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  if (!canViewTeam) {
    return (
      <div>
        <Header>
          <div className="content">
            <div className="ol-header">
              <div className="ol-hd">
                <div>
                  <div className="ol-title">Team Members</div>

                  <div className="ol-sub">
                    <span className="dot"></span> Organisation
                  </div>
                </div>
              </div>
            </div>

            <div className="tm-wrap">
              <div className="card-base tm-table-card">
                <div
                  style={{
                    padding: "50px",
                    textAlign: "center",
                  }}
                >
                  <div className="ol-empty-ico">
                    <i className="bi bi-shield-lock"></i>
                  </div>

                  <h4>Access Restricted</h4>

                  <p>You do not have permission to view team members.</p>
                </div>
              </div>
            </div>
          </div>
        </Header>

        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Header>
        <div className="content">
          <div className="ol-header">
            <div className="ol-hd">
              <button
                type="button"
                className="ol-back"
                id="tmBack"
                title="Go back"
                onClick={() => window.history.back()}
              >
                <i className="bi bi-arrow-left"></i>
              </button>

              <div>
                <div className="ol-title">Team Members</div>

                <div className="ol-sub">
                  <span className="dot"></span> Organisation{" "}
                  <span
                    className="dot"
                    style={{
                      background: "var(--t4)",
                    }}
                  ></span>{" "}
                  Team &amp; Access
                </div>
              </div>
            </div>

            <div className="ol-actions">
              {canAddTeam && (
                <a href="/create_team_user?id=new" className="ol-btn primary">
                  <i className="bi bi-plus-lg"></i> Add Member
                </a>
              )}
            </div>
          </div>

          <div className="tm-wrap" id="tmRoot">
            <div className="card-base ol-search-card tm-toolbar">
              <div className="ol-search-row">
                <div className="ol-search" id="tmSearch">
                  <i className="bi bi-search"></i>

                  <input
                    type="text"
                    placeholder="Search name, email, mobile or role..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />

                  <span className="kbd">⌘K / Ctrl K</span>
                </div>

                <div className="tm-tabs" id="tmTabs">
                  <button
                    type="button"
                    className={`ol-snav ${activeTab === "all" ? "on" : ""}`}
                    onClick={() => setActiveTab("all")}
                  >
                    <span className="tx">All</span>

                    <span className="cnt" id="tmCntAll">
                      {counts.all}
                    </span>
                  </button>

                  <button
                    type="button"
                    className={`ol-snav ${activeTab === "active" ? "on" : ""}`}
                    onClick={() => setActiveTab("active")}
                  >
                    <span className="tx">Active</span>

                    <span className="cnt" id="tmCntActive">
                      {counts.active}
                    </span>
                  </button>

                  <button
                    type="button"
                    className={`ol-snav ${
                      activeTab === "disabled" ? "on" : ""
                    }`}
                    onClick={() => setActiveTab("disabled")}
                  >
                    <span className="tx">Disabled</span>

                    <span className="cnt" id="tmCntDisabled">
                      {counts.disabled}
                    </span>
                  </button>
                </div>
              </div>
            </div>

            <div className="card-base tm-table-card">
              <div className="tm-table-wrap">
                <table className="tm-table" id="tmTable">
                  <thead>
                    <tr>
                      <th className="tm-idx-th">#</th>

                      <th>Member</th>

                      <th>Role</th>

                      <th>Access</th>

                      <th>Status</th>

                      <th className="tm-actions-th"></th>
                    </tr>
                  </thead>

                  <tbody id="tmTbody">
                    {loading ? (
                      <tr>
                        <td
                          colSpan="6"
                          style={{
                            textAlign: "center",
                            padding: "40px",
                          }}
                        >
                          Loading team members...
                        </td>
                      </tr>
                    ) : (
                      filteredMembers.map((member, index) => (
                        <tr
                          key={member.id}
                          className={
                            member.status === "disabled" ? "is-disabled" : ""
                          }
                        >
                          <td className="tm-idx-th">
                            <span className="tm-idx">{index + 1}</span>
                          </td>

                          <td className="tm-name-td">
                            <div className="tm-name-cell">
                              <span className="tm-av">
                                {initials(member.name)}
                              </span>

                              <span>
                                <span className="tm-name-main">
                                  {member.name}
                                </span>

                                <span className="tm-name-sub">
                                  {member.email}

                                  {member.mobile ? ` · ${member.mobile}` : ""}
                                </span>
                              </span>
                            </div>
                          </td>

                          <td>
                            <span className="tm-role">
                              <i className="bi bi-person"></i>
                              {member.role}
                            </span>
                          </td>

                          <td>
                            <div className="tm-access">
                              <span className="tm-lv lv-view">
                                <i className="bi bi-grid"></i>
                                {member.selectedModules} Modules
                              </span>

                              <span className="tm-access-sub">
                                of {MODULES.length} available
                              </span>
                            </div>
                          </td>

                          <td>
                            <label
                              className="oa-inc"
                              title={
                                member.status === "active"
                                  ? "Disable member"
                                  : "Enable member"
                              }
                            >
                              <input
                                type="checkbox"
                                checked={member.status === "active"}
                                disabled={!canAddTeam}
                                onChange={(e) =>
                                  handleStatusChange(member, e.target.checked)
                                }
                              />

                              <span className="oa-inc-slider"></span>
                            </label>
                          </td>

                          <td className="tm-actions-th">
                            <div className="tm-row-acts">
                              {canAddTeam && (
                                <>
                                  <button
                                    type="button"
                                    className="tm-row-btn"
                                    title="Edit member"
                                    onClick={() => handleEdit(member)}
                                  >
                                    <i className="bi bi-pencil-square"></i>
                                  </button>

                                  <button
                                    type="button"
                                    className="tm-row-btn"
                                    title="Duplicate member"
                                    onClick={() => handleDuplicate(member)}
                                  >
                                    <i className="bi bi-copy"></i>
                                  </button>
                                </>
                              )}

                              {canDeleteTeam && (
                                <button
                                  type="button"
                                  className="tm-row-btn danger"
                                  title="Delete member"
                                  onClick={() => handleDelete(member)}
                                >
                                  <i className="bi bi-trash3"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                {!loading && filteredMembers.length === 0 && (
                  <div className="ol-empty" id="tmEmpty">
                    <div className="ol-empty-ico">
                      <i className="bi bi-people"></i>
                    </div>

                    <h4>No team members yet</h4>

                    <p>
                      {search
                        ? "No team members match your search."
                        : "Add your first team member to give them access to orders, shipments, warehouses and tools."}
                    </p>

                    {!search && canAddTeam && (
                      <div className="ol-empty-acts">
                        <a
                          href="/create_team_user?id=new"
                          className="ol-btn primary"
                        >
                          <i className="bi bi-plus-lg"></i> Add your first
                          member
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="card-base tm-legend">
              <div className="tm-legend-title">Module access</div>

              <div className="tm-legend-row">
                <span className="tm-lv lv-view">
                  <i className="bi bi-check-circle"></i> Enabled
                </span>

                <span className="tm-legend-desc">
                  Module is visible in the team member sidebar
                </span>

                <span className="tm-lv lv-none">
                  <i className="bi bi-dash-circle"></i> Disabled
                </span>

                <span className="tm-legend-desc">
                  Module is hidden from the team member sidebar
                </span>
              </div>
            </div>
          </div>
        </div>
      </Header>

      <Footer />
    </div>
  );
};

export default Team;
